import { Schema } from 'prosemirror-model';
/**
 * The ProseMirror schema, defined here rather than in the writer app.
 *
 * Reason: the Markdown bridge is what guarantees round-trip fidelity, so the
 * bridge owns the node contract. The writer builds TipTap extensions on top of
 * these exact node and mark names; it does not get to invent its own.
 *
 * `unknownBlock` / `unknownInline` are the safety net. Any mdast node this
 * schema does not model is preserved verbatim as JSON instead of being dropped,
 * so an unrecognised construct survives an edit-and-save cycle untouched. A
 * lossy serializer is the worst failure this product can have.
 */
export const docSchema = new Schema({
    nodes: {
        doc: { content: 'block+' },
        paragraph: { content: 'inline*', group: 'block', parseDOM: [{ tag: 'p' }] },
        heading: {
            attrs: { level: { default: 1 } },
            content: 'inline*',
            group: 'block',
            defining: true,
        },
        codeBlock: {
            /**
             * `meta` is everything after the language on the fence line.
             *
             * ```` ```js title="server.ts" {2-4} showLineNumbers ````
             *
             * It was dropped on the way in, which made opening a page in the editor
             * once delete the filename, the highlighted lines and the line-number
             * flag from every code block on it. That is not a missing feature, it is
             * content loss — and every code-block feature the product wants is
             * expressed in this string, so carrying it is the whole job.
             *
             * Held verbatim rather than parsed. The renderer owns the grammar, and a
             * parser here would have to be kept in step with it or start quietly
             * discarding options it did not recognise.
             */
            attrs: { language: { default: null }, meta: { default: null } },
            content: 'text*',
            marks: '',
            group: 'block',
            code: true,
            defining: true,
        },
        blockquote: { content: 'block+', group: 'block', defining: true },
        bulletList: { attrs: { spread: { default: false } }, content: 'listItem+', group: 'block' },
        orderedList: {
            attrs: { start: { default: 1 }, spread: { default: false } },
            content: 'listItem+',
            group: 'block',
        },
        listItem: {
            attrs: { checked: { default: null } },
            content: 'block+',
            defining: true,
        },
        horizontalRule: { group: 'block' },
        table: { content: 'tableRow+', group: 'block', attrs: { align: { default: [] } } },
        tableRow: { content: 'tableCell+' },
        tableCell: { attrs: { header: { default: false } }, content: 'inline*' },
        // Directive-backed blocks. Each maps to a `:::name` container in Markdown.
        // `type: null` means the author wrote `:::hint` with no attribute. The
        // renderer defaults it to `note`; the model never rewrites it.
        // `title` because every platform we import from has one: Docusaurus
        // `:::note[Title]`, MkDocs `!!! note "Title"`, VitePress `::: tip Title`,
        // Mintlify's component children. Without it all three importers had to
        // flatten a title into a bold first line, which reads as content rather
        // than as a label and cannot be styled or restyled.
        hint: {
            attrs: { type: { default: null }, title: { default: null } },
            content: 'block+',
            group: 'block',
        },
        tabs: {
            attrs: {
                /**
                 * A name that ties this group to the others like it on the page.
                 *
                 * Choosing "npm" in one tab group should choose it in every group that
                 * asks the same question — which is what a reader means when they pick
                 * a package manager once and read the rest of the page. Null is the
                 * ordinary case: a group that answers only its own question.
                 */
                group: { default: null },
            },
            content: 'tab+',
            group: 'block',
        },
        tab: {
            attrs: {
                title: { default: null },
                /**
                 * What this tab IS, for syncing — `npm`, `macos`, `python`.
                 *
                 * Separate from the title because a title is words for a reader
                 * ("npm (recommended)") and this is the thing two groups have in
                 * common. Null falls back to the title, which is right for the many
                 * groups whose labels already are the values.
                 */
                value: { default: null },
                /** Opens on this one instead of the first. At most one per group. */
                default: { default: false },
            },
            content: 'block+',
        },
        expandable: { attrs: { title: { default: null } }, content: 'block+', group: 'block' },
        // A numbered procedure. `step+` rather than `block*` because a steps
        // container holding loose paragraphs has lost the thing that makes it a
        // procedure, and the editor should not be able to create that state.
        steps: { content: 'step+', group: 'block' },
        step: { attrs: { title: { default: null } }, content: 'block+' },
        // An image with an optional caption. The caption is CONTENT, not an
        // attribute, so it can hold a link or emphasis — GitBook wraps its own
        // captions in <p>, which is a paragraph whether it admits it or not.
        // `width` is kept because the source sets it on 34 of 35 real figures, and
        // dropping it would silently reflow every screenshot to full width.
        figure: {
            attrs: {
                src: { default: null },
                alt: { default: null },
                width: { default: null },
                /**
                 * The same picture drawn for a dark ground, when the author has one.
                 *
                 * One figure with two sources rather than two figures: they are the
                 * same illustration, they share a caption, and a reader must never see
                 * both. An import that produced two figures showed both, stacked,
                 * which is what this exists to stop.
                 */
                srcDark: { default: null },
            },
            content: 'block*',
            group: 'block',
        },
        // A video or an interactive demo. `content: 'block*'` so a caption or a
        // fallback link can live inside it, and so an embed whose provider we do not
        // recognise still shows something.
        embed: { attrs: { url: { default: null } }, content: 'block*', group: 'block' },
        cards: { content: 'card+', group: 'block' },
        card: {
            attrs: { title: { default: null }, href: { default: null }, icon: { default: null } },
            content: 'block*',
        },
        // A downloadable attachment. `content: 'block*'` for an optional caption,
        // same reasoning as `figure` — size and upload metadata belong to the asset
        // library (§12), not to the reference a page makes to it, so they are not
        // attributes here.
        file: {
            attrs: { src: { default: null }, name: { default: null } },
            content: 'block*',
            group: 'block',
        },
        // A call-to-action link. The label is CONTENT (`inline*`), not an attribute,
        // so it can carry emphasis; written as a leaf directive in Markdown because
        // that content is phrasing, not a block.
        button: {
            attrs: { href: { default: null }, style: { default: null } },
            content: 'inline*',
            group: 'block',
        },
        // A set of fenced code blocks presented as tabs, one per language. `content:
        // 'codeBlock+'` rather than `block+` for the same reason `steps` requires
        // `step+`: a code group holding something that is not code has lost the
        // thing that makes it one, and the editor should not be able to build that.
        codeGroup: { content: 'codeBlock+', group: 'block' },
        /**
         * One field of an API reference: a name, a type, whether it is required,
         * a default, and prose. Modelled by NAME rather than left generic,
         * because an API page is mostly these and a page that renders them as
         * unlabelled prose is a page nobody can read an API from.
         */
        field: {
            attrs: {
                name: { default: null },
                type: { default: null },
                required: { default: false },
                default: { default: null },
                /** Where the field lives: `query`, `path`, `body`, `header`. */
                in: { default: null },
            },
            content: 'block*',
            group: 'block',
        },
        /**
         * A reference to a shared block in `snippets/<name>.md`. An atom: the
         * body is not IN the document — it is shown where referenced and edited
         * in its own file, so one edit lands everywhere at once.
         */
        snippet: { attrs: { name: {} }, group: 'block', atom: true },
        /** Any container/leaf directive this schema does not model by name. */
        genericDirective: {
            attrs: { name: {}, attributes: { default: {} }, kind: { default: 'container' } },
            content: 'block*',
            group: 'block',
        },
        unknownBlock: { attrs: { mdast: {} }, group: 'block', atom: true },
        text: { group: 'inline' },
        image: {
            inline: true,
            attrs: { src: {}, alt: { default: null }, title: { default: null } },
            group: 'inline',
            draggable: true,
        },
        hardBreak: { inline: true, group: 'inline', selectable: false },
        unknownInline: { inline: true, attrs: { mdast: {} }, group: 'inline', atom: true },
    },
    marks: {
        strong: {},
        em: {},
        code: {},
        strike: {},
        // Markdown has no bare syntax for either of these — no delimiter pair is
        // free the way `_x_`/`` `x` `` are — so both round-trip as directives
        // (`:underline[x]`, `:highlight[x]`) rather than inventing one that would
        // silently reinterpret unrelated text.
        underline: {},
        highlight: {},
        link: {
            attrs: { href: {}, title: { default: null } },
            inclusive: false,
        },
    },
});
//# sourceMappingURL=pm-schema.js.map